/**
 * Which colour a thing is.
 *
 * Originally "which colour a *goal* is": goal was the only key that could cover every task, so it
 * won by default. Projects can now carry a colour too, and a task can carry its own, so the
 * question became an ordering rather than a choice — and the answer is a list, not a chain of
 * `if`s, because the next container to want a colour should cost one entry rather than a rewrite.
 *
 * ## The order, and why
 *
 * A task's own colour, then its project's, then its goal's, then automatic. Project beats goal
 * because a task has at most one project and can serve several goals, so the project is the less
 * ambiguous source — and because every goal is auto-coloured already, a goal-wins rule would mean
 * project colours essentially never appeared.
 *
 * ## Five, and why not more
 *
 * A research agent simulated deuteranopia and protanopia and measured pairwise CIELAB separation
 * across an eight-colour set: the closest pair collapsed to ΔE 0.3, which is to say indis-
 * tinguishable. Searching subsets gave min-ΔE of 0.3 at eight, 5.6 at six and 9.8 at five. Under
 * deuteranopia the gamut reduces to a blue–yellow axis plus lightness, which supports about four
 * or five categories and no more. Five it is, with lightness deliberately staggered so that when
 * hue collapses a lightness step survives.
 *
 * Red and green are absent on purpose: `--ok` and `--bad` already drive pills, progress fills,
 * rings and banners, so a red goal would read as a failing goal.
 *
 * ## Names, not hexes
 *
 * The stored value is `"violet"`. The stylesheet owns the actual colour, which means the token
 * test can see it, dark mode is automatic, and changing the palette later is one file. Storing a
 * hex would freeze today's palette into the user's rows and put an unthemed literal into the DOM
 * — which is exactly the bug the habit stripe has, where a colour of `#fbfbfa` is invisible
 * against a `#fbfbfa` page.
 */

/**
 * Duplicated from `packages/core/src/db/schema/goals.ts`, which is the source of truth.
 *
 * The browser bundle deliberately does not depend on `@planner/core` — `TaskStatus` and the rest
 * are duplicated across the same boundary for the same reason.
 */
export const GOAL_COLORS = ['violet', 'sky', 'rose', 'teal', 'amber'] as const;
export type GoalColor = (typeof GOAL_COLORS)[number];

/**
 * The colour to draw a goal in.
 *
 * `null` means auto, and auto is deterministic rather than grey: nothing on this account has ever
 * had a colour set, so a scheme that starts blank would show the owner exactly what they have
 * now. Keyed on `sortOrder` rather than a hash of the id — five goals hashed into five buckets
 * collide with probability 1 − 5!/5⁵ ≈ **96%**, where `sortOrder` is distinct by construction.
 *
 * An unrecognised value (a hex from before this existed, or anything the API's old free-form
 * validation accepted) resolves to auto rather than reaching the DOM.
 */
export function resolveGoalColor(color: string | null, position: number): GoalColor {
  if (color !== null && (GOAL_COLORS as readonly string[]).includes(color)) {
    return color as GoalColor;
  }
  const index = ((position % GOAL_COLORS.length) + GOAL_COLORS.length) % GOAL_COLORS.length;
  return GOAL_COLORS[index]!;
}

/** The class that paints it. Never an inline style — see the note in `styles.css`. */
export const goalColorClass = (color: string | null, position: number): string =>
  `goal-c-${resolveGoalColor(color, position)}`;

/**
 * Every goal in the tree, with the colour it should be drawn in.
 *
 * Position in the flattened tree, **not** `sort_order`. The research agent proposed `sort_order`
 * on the grounds that it is "collision-free by construction", which was measured on a sandbox: on
 * the owner's real accounts every goal has `sort_order = 0`, so all five would have come out the
 * same colour. Exactly the trap this palette exists to avoid — the first five goals must be five
 * different colours the moment it ships.
 *
 * Tree position reshuffles if you reorder your goals, which is discoverable and reversible, and
 * an explicit choice pins a goal's colour against any reshuffling.
 */
export function goalColorMap<
  T extends { goal: { id: string; color: string | null }; children: T[] },
>(nodes: readonly T[]): Map<string, string> {
  const out = new Map<string, string>();
  let position = 0;
  const walk = (list: readonly T[]): void => {
    for (const node of list) {
      out.set(node.goal.id, goalColorClass(node.goal.color, position));
      position += 1;
      walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

/**
 * Where a colour can come from, in the order it is asked.
 *
 * A list rather than a chain of `if`s. Every source names itself, which is what lets the UI say
 * "from Norway" instead of just showing a swatch — and what makes adding a source later one entry
 * rather than a rewrite through every consumer.
 */
export interface ColorSource {
  /** The stored choice, or null to fall through to the next source. */
  color: string | null;
  /** What to tell the user this colour came from. */
  from: string;
}

export interface ResolvedColor {
  /** The class to put on the element, or null when nothing has an opinion. */
  colorClass: string | null;
  /** Which source won, for a tooltip or a caption. */
  from: string;
}

/**
 * Ask each source in turn, and say which one answered.
 *
 * Resolved at read time and never written back. That is the whole reason "tasks without a colour
 * of their own retint when their project gets one" is free: nothing stored the old answer, so
 * there is nothing to go stale. Writing the resolved value would also destroy the distinction
 * between a colour somebody chose and one that happened to be inherited, and the next recolour
 * would then either skip the deliberate ones or overwrite them.
 */
export function resolveColor(
  sources: readonly ColorSource[],
  /** Used only if nothing has an opinion; pass null to leave it uncoloured. */
  autoPosition: number | null = null,
): ResolvedColor {
  for (const source of sources) {
    if (source.color !== null && (GOAL_COLORS as readonly string[]).includes(source.color)) {
      return { colorClass: `goal-c-${source.color}`, from: source.from };
    }
  }
  if (autoPosition === null) return { colorClass: null, from: 'nothing' };
  return { colorClass: goalColorClass(null, autoPosition), from: 'auto' };
}

/**
 * A task's colour, including the one its goal was *given* rather than chosen.
 *
 * `resolveColor` only answers from colours somebody set. A goal with no explicit colour still has
 * one on screen — `goalColorMap` assigns it by tree position, which is what the goal chip, the
 * board card and the Goals screen all draw. The two list surfaces asked the goal for its *raw*
 * colour instead, got `null`, and rendered no band at all.
 *
 * The visible result was a row whose chip was violet and whose 3px band was missing, on the
 * commonest kind of goal there is: nobody sets colours by hand. The band is the whole of what the
 * owner asked for here ("for color 3px bar is enough for lists"), and it was absent unless you had
 * gone out of your way to colour something.
 *
 * Passing the goal's assigned class rather than re-deriving it keeps the band and the chip the
 * same colour by construction, which is the point of resolving colour instead of copying it.
 */
export function resolveTaskColor(
  sources: readonly ColorSource[],
  /** The first goal's class from `goalColorMap`, if it has one. */
  goalClass: string | null,
): ResolvedColor {
  const chosen = resolveColor(sources);
  if (chosen.colorClass !== null) return chosen;
  return goalClass ? { colorClass: goalClass, from: 'its goal' } : chosen;
}

/**
 * The nearest ancestor with a colour, walking up.
 *
 * A subproject set to Auto should take its parent's colour rather than falling back to grey — the
 * ancestor chain *is* the answer, not a fallback from it. `hops` is a cycle guard: the tree is
 * guarded at the service, and a corrupt parent pointer must not hang the browser.
 */
export function nearestColored<
  T extends { id: string; color: string | null; parentId: string | null },
>(start: T | null, byId: ReadonlyMap<string, T>, label: (node: T) => string): ColorSource | null {
  let node = start;
  for (let hops = 0; node && hops < 30; hops += 1) {
    if (node.color !== null) return { color: node.color, from: label(node) };
    node = node.parentId ? (byId.get(node.parentId) ?? null) : null;
  }
  return null;
}
